import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GET_ACTIVE_DOCUMENTS } from '../../graphql/queries';
import { Poetry } from '../../types';

export type FollowUpVisitTag = {
  carePlanId: string;
  displayDate: string;
  type: string;
};

/** Shape returned by getActiveDocuments (DWF does not include visit display fields). */
export interface ActiveDocumentFromApi {
  uuid: string;
  studyId?: string;
  patientId: string;
  carePlanId?: string;
  careActivityId?: string;
  typeId: string;
  name: string;
  status: string;
}

/** Document after poetry enrichment — used by list UI. */
export type DocumentRecord = ActiveDocumentFromApi & {
  followUpVisit?: FollowUpVisitTag;
};

interface GetActiveDocumentsResponse {
  getActiveDocuments: ActiveDocumentFromApi[];
}

type GetActiveDocumentsVariables = { studyId?: string; carePlanId?: string };

export const useGetActiveDocuments = (
  studyId?: string,
  poetryMetadata?: Poetry,
): {
  documents?: DocumentRecord[];
  loading: boolean;
  failedFetchIds: string[];
  refetch: () => Promise<void>;
} => {
  const client = useApolloClient();
  const [fetchedDocuments, setFetchedDocuments] = useState<ActiveDocumentFromApi[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [failedFetchIds, setFailedFetchIds] = useState<string[]>([]);

  const carePlanIds = useMemo(() => {
    if (!poetryMetadata) return [];
    const visits = poetryMetadata.followUpVisits ?? [];
    return [
      poetryMetadata.carePlanId,
      ...visits.map((visit) => visit.carePlanId),
    ].filter((id): id is string => Boolean(id));
  }, [poetryMetadata?.carePlanId, poetryMetadata?.followUpVisits]);

  const documents = useMemo((): DocumentRecord[] | undefined => {
    if (fetchedDocuments === undefined) {
      return undefined;
    }
    const visits = poetryMetadata?.followUpVisits ?? [];
    if (visits.length === 0) {
      return fetchedDocuments;
    }
    return fetchedDocuments.map((document) => {
      const matchedVisit = document.carePlanId
        ? visits.find((visit) => visit.carePlanId === document.carePlanId)
        : undefined;
      if (!matchedVisit) return document;
      return {
        ...document,
        followUpVisit: {
          carePlanId: matchedVisit.carePlanId,
          displayDate: matchedVisit.displayDate,
          type: matchedVisit.type,
        },
      };
    });
  }, [fetchedDocuments, poetryMetadata?.followUpVisits]);

  const loadActiveDocuments = useCallback(async () => {
    setLoading(true);
    setFailedFetchIds([]);
    try {
      let requests: { fetchId: string; variables: GetActiveDocumentsVariables }[] = [];
      // Mutually exclusive: either one query per care plan id from poetry, or a single study query — never both.
      if (carePlanIds.length > 0) {
        requests = carePlanIds.map((carePlanId) => ({
          fetchId: carePlanId,
          variables: { carePlanId },
        }));
      } else if (studyId) {
        requests = [{ fetchId: studyId, variables: { studyId } }];
      }

      if (requests.length === 0) {
        setFetchedDocuments([]);
      } else {
        const results = await Promise.all(
          requests.map(async ({ fetchId, variables }) => {
            try {
              const value = await client.query<
                GetActiveDocumentsResponse,
                GetActiveDocumentsVariables
              >({
                query: GET_ACTIVE_DOCUMENTS,
                variables,
                fetchPolicy: 'no-cache',
              });
              return { fetchId, ok: true as const, value };
            } catch {
              return { fetchId, ok: false as const };
            }
          }),
        );

        const failed: string[] = [];
        const fulfilledLists: ActiveDocumentFromApi[][] = [];

        results.forEach((result) => {
          if (result.ok) {
            fulfilledLists.push(result.value.data?.getActiveDocuments ?? []);
          } else {
            failed.push(result.fetchId);
          }
        });

        setFetchedDocuments(fulfilledLists.flat());
        setFailedFetchIds(failed);
      }
    } finally {
      setLoading(false);
    }
  }, [client, studyId, carePlanIds]);

  useEffect(() => {
    loadActiveDocuments();
  }, [loadActiveDocuments]);

  return {
    documents,
    loading,
    failedFetchIds,
    refetch: loadActiveDocuments,
  };
};
